import { types, flow, castToSnapshot } from 'mobx-state-tree';
import { navigate } from 'js/services/Network';
import { pick } from 'lodash';

import { makeRequest, url } from 'js/services/Network';
import analytics from 'js/services/Analytics';
import { castEventToValue } from 'js/services/FormTools';

import ShippingSelectorState from 'js/stores/ShippingSelectorState';
import SteppedPage from 'js/stores/SteppedPage';

import Address from 'js/stores/types/Address';
import CreditCard from 'js/stores/types/CreditCard';
import PurchaseToken from 'js/stores/types/PurchaseToken';
import ShippingDetail from 'js/stores/types/ShippingDetail';

const TokenConfirmPageState = types
  .model('TokenConfirmPageState', {
    alias: types.string,
    size: types.maybe(types.string),
    error: types.maybeNull(types.string),
    wasSubmitted: false,
    modalIsVisible: false,
    modalDisplay: '',
    fee: 0,
    full_name: '',
    phone_number: '',
    shippingSelector: types.optional(ShippingSelectorState, {}),
    purchase_token: types.maybe(PurchaseToken),
    credit_card: types.optional(CreditCard, {}),
    address: types.optional(Address, {}),
    shipping_service: types.maybeNull(ShippingDetail),
    bonus_applied: 0,
    bonus_available: 0,
    applyingBonus: false
  })
  .actions(self => ({
    getStepRoute(step) {
      return url('token-confirm-page', {
        alias: self.alias,
        step,
      });
    },

    initStep(step) {
      self.modalIsVisible = false;
      return self;
    },

    load: flow(function* load() {
      const response = yield makeRequest('token-confirm-data', self);
      self.fee = response.data.fee;
      self.purchase_token = response.data.purchase_token;
      self.credit_card = response.data.credit_card;
      self.address = response.data.address;
      self.full_name = response.data.full_name || '';
      self.phone_number = response.data.phone_number || '';
      self.bonus_available = response.data.bonus_available;
      self.bonus_applied = response.data.bonus_applied;

      self.shippingSelector.initialize({
        alias: self.alias,
        size: self.size,
        destination: self.address,
        onChange: self.setShippingService,
      });

      return response;
    }),

    submit: flow(function* submit() {
      let response;
      if (!self.isSubmitting) {
        self.isSubmitting = true;
        self.error = null;

        try {
          //analytics.track('BC_clicked confirm');
          response = yield makeRequest(
            'token-confirm',
            self,
            pick(self, ['full_name', 'phone_number', 'credit_card', 'address', 'shipping_service']),
          );
          navigate(url('status-page', self));
        } catch (error) {
          self.error = error.response.data.message || error.response.statusText;
        }

        self.isSubmitting = false;
      }
      return response;
    }),

    editDeliveryAddress() {
      self.modalDisplay = 'delivery';
      self.modalIsVisible = true;
    },
    editShipping() {
      self.modalDisplay = 'shipping';
      self.shipping_service === null && self.shippingSelector.load();
      self.modalIsVisible = true;
    },
    editPaymentMethod() {
      self.modalDisplay = 'payment';
      self.modalIsVisible = true;
    },
    applyBonus: flow(function* applyBonus(e) {
      e.preventDefault();
      self.applyingBonus = true;
      const response = yield makeRequest('apply-bonus', self);
      self.applyingBonus = false;
      if(response.data === true){
        self.bonus_applied = self.bonus_available;
        self.bonus_available = 0;

        analytics.track('Reward Credit Applied', {
          'Source Address': '',
          'Destination Address': self.address,
          'Shipping Option': self.shipping_service,
          'Shipping Cost': self.fee,
          'Item Price': self.purchase_token.price,
          'Item Name': self.purchase_token.title,
          'Item Size': self.size,
          'Event Initiated by': 'buyer'
        });

      }
    }),
    openModal() {
      self.modalIsVisible = true;
      return self;
    },
    closeModal() {
      self.modalIsVisible = false;
      return self;
    },
    setShippingService(shippingService) {
      self.shipping_service = shippingService;
      return self;
    },
    creditCardSaved(creditCard) {
      self.credit_card.setCreditCard(castToSnapshot(creditCard));
      self.modalIsVisible = false;
    },
    addressSaved(savedAddress) {
      self.address.setAddress(castToSnapshot(savedAddress));
      self.shipping_service = null;
      self.modalIsVisible = false;
    },
    setFullName(full_name) {
      self.full_name = castEventToValue(full_name);
      return self;
    },
    setPhoneNumber(phone_number) {
      self.phone_number = castEventToValue(phone_number);
      return self;
    },
  }));

export default types.compose(
  SteppedPage,
  TokenConfirmPageState,
);
